import React, { useRef, useMemo, useState, useEffect, Suspense } from 'react';
import PropTypes from 'prop-types';
import { PieChart } from 'echarts/charts';
import {
    GridComponent,
    LegendComponent,
    TitleComponent,
    TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../../../providers/AppProvider';
import { Spinner } from 'react-bootstrap';

// Carga diferida del componente ReactEchart con prioridad
const ReactEchart = React.lazy(() =>
    // Añadir una pequeña demora para evitar conflictos con otros renderizados
    new Promise(resolve => {
        // Priorizar el renderizado de la UI principal primero
        setTimeout(() => {
            resolve(import('../../../../../components/common/ReactEchart'));
        }, 100);
    })
);

// Registrar solo los componentes necesarios
echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    PieChart,
    CanvasRenderer,
    LegendComponent
]);

// Extraer la función getOptions fuera del componente para evitar recreaciones
const getOptions = (data, getThemeColor) => {
    if (!getThemeColor || typeof getThemeColor !== 'function') {
        console.warn("getThemeColor is not a function or is undefined");
        return {};
    }

    return {
        tooltip: {
            trigger: 'item',
            confine: true,
            padding: [7, 10],
            backgroundColor: '#fff',
            borderColor: '#ccc',
            textStyle: { color: '#000' },
            borderWidth: 1,
            transitionDuration: 0,
            formatter: function (params) {
                const value = params.data.value || 0;
                const count = params.data.target || 0;
                return `<strong>${params.data.name}:</strong> ${params.percent}% (${count})`;
            }
        },
        legend: { show: false },
        animation: false, // Desactivar animación para mejorar rendimiento
        series: [
            {
                name: 'Alertas',
                type: 'pie',
                radius: ['100%', '67%'],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderWidth: 2,
                    borderColor: '#fff'
                },
                label: {
                    show: false,
                    formatter: '{b}: {d}%'
                },
                emphasis: {
                    scale: false
                },
                labelLine: { show: false },
                data: data.map(alert => ({
                    value: alert.amount,
                    name: alert.title,
                    target: alert.target,
                    itemStyle: { color: alert.color },
                    alertType: alert.title
                }))
            }
        ]
    };
};

// Estados de carga más granulares para mejor feedback
const LOADING_STATES = {
    INITIAL: 'initial',   // Estado inicial
    DATA_READY: 'data_ready',   // Datos listos pero gráfico aún cargando
    RENDERING: 'rendering',     // Renderizando el gráfico
    COMPLETED: 'completed'      // Todo completo
};

const CardGraphOutHour = ({ data, onSelectAlertType, total }) => {

    const chartRef = useRef(null);
    const { getThemeColor } = useAppContext() || {};
    const [loadingState, setLoadingState] = useState(LOADING_STATES.INITIAL);
    const [lastRenderTime, setLastRenderTime] = useState(0);

    // Limitar el número máximo de segmentos para mejorar rendimiento
    const MAX_SEGMENTS = 6; // Reducido de 8 a 6 para mejor rendimiento visual

    // Memoización con dependencias específicas para evitar cálculos repetidos
    const dataSignature = useMemo(() =>
        data.map(d => `${d.title}:${d.target}`).sort().join('|'),
        [data]
    );

    const chartData = useMemo(() => {
        if (!data || data.length === 0) return [];

        // Si hay pocos datos, usarlos directamente
        if (data.length <= MAX_SEGMENTS) {
            return data;
        }

        // Si hay muchos tipos de alertas, agrupar las menos frecuentes como "Otros"
        // Ordenar por cantidad (target)
        const sortedData = [...data].sort((a, b) => b.target - a.target);

        // Tomar los primeros MAX_SEGMENTS-1 elementos
        const topData = sortedData;//.slice(0, MAX_SEGMENTS - 1);

        return [
            ...topData,
        ];
    }, [dataSignature, total, MAX_SEGMENTS]); // Dependencia mejorada

    // Memoizar las opciones del gráfico para evitar recálculos
    const chartOptions = useMemo(() =>
        getOptions(chartData, getThemeColor),
        [chartData, getThemeColor]
    );

    // Detectar cuando los datos están listos
    useEffect(() => {
        if (data && data.length > 0 && loadingState === LOADING_STATES.INITIAL) {
            setLoadingState(LOADING_STATES.DATA_READY);
        }
    }, [data, loadingState]);

    // Prevenir actualizaciones demasiado frecuentes (throttling)
    useEffect(() => {
        if (loadingState === LOADING_STATES.DATA_READY) {
            const now = Date.now();
            // Solo actualizar si han pasado al menos 300ms desde la última renderización
            if (now - lastRenderTime > 300) {
                setLoadingState(LOADING_STATES.RENDERING);
                setLastRenderTime(now);
                // Programar el cambio a COMPLETED después de un breve tiempo
                const timer = setTimeout(() => {
                    setLoadingState(LOADING_STATES.COMPLETED);
                }, 100);
                return () => clearTimeout(timer);
            }
        }
    }, [loadingState, lastRenderTime]);

    // Eventos memorizados para evitar recreaciones en cada render
    const events = useMemo(() => ({
        click: (params) => {
            if (params.data && params.data.alertType && params.data.alertType !== 'Otros') {
                onSelectAlertType(params.data.alertType);
            }
        }
    }), [onSelectAlertType]);

    // Renderizar un placeholder según el estado de carga
    if (loadingState === LOADING_STATES.INITIAL) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '12.5rem' }}>
                <Spinner animation="border" variant="primary" size="sm" />
                <span className="ms-2">Inicializando gráfico...</span>
            </div>
        );
    }

    // Renderizar una versión esqueleto mientras los datos están listos pero aún no se completa
    if (loadingState === LOADING_STATES.DATA_READY) {
        return (
            <div className="position-relative py-2" style={{ height: '12.5rem' }}>
                <div className="d-flex justify-content-center align-items-center h-100">
                    <div className="text-center">
                        <Spinner animation="border" variant="primary" size="sm" />
                        <p className="mb-0 mt-2">Preparando visualización...</p>
                    </div>
                </div>
                <div className="position-absolute top-50 start-50 translate-middle text-center">
                    <p className="fs-10 mb-0 text-400 font-sans-serif fw-medium">Total</p>
                    <p className="fs-6 mb-0 font-sans-serif fw-medium mt-n2">{total}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="position-relative py-2">
            <Suspense fallback={
                <div className="d-flex justify-content-center align-items-center" style={{ height: '12.5rem' }}>
                    <Spinner animation="border" variant="primary" size="sm" />
                    <span className="ms-2">Cargando biblioteca de gráficos...</span>
                </div>
            }>
                <ReactEchart
                    ref={chartRef}
                    echarts={echarts}
                    option={chartOptions}
                    onEvents={events}
                    style={{ height: '12.5rem' }}
                    notMerge={true}
                    lazyUpdate={true}
                    opts={{
                        renderer: 'canvas',
                        devicePixelRatio: window.devicePixelRatio || 1
                    }}
                />
            </Suspense>
            <div className="position-absolute top-50 start-50 translate-middle text-center">
                <p className="fs-6 mb-0 font-sans-serif fw-medium mt-n2">{total}</p>
                <p className="fs-10 mb-0 text-400 font-sans-serif fw-medium">Horas</p>
            </div>
        </div>
    );
};

CardGraphOutHour.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            amount: PropTypes.number.isRequired,
            target: PropTypes.number.isRequired,
            color: PropTypes.string.isRequired
        })
    ).isRequired,
    onSelectAlertType: PropTypes.func.isRequired,
    total: PropTypes.number.isRequired
};

// Implementación avanzada de memoización con comparación profunda
const arePropsEqual = (prevProps, nextProps) => {
    // Verificar si cambiaron properties escalares
    if (prevProps.total !== nextProps.total) return false;

    // Verificar si cambió la función callback (comparación por referencia)
    if (prevProps.onSelectAlertType !== nextProps.onSelectAlertType) return false;

    // Si la cantidad de alertas es diferente, definitivamente son diferentes
    if (prevProps.data.length !== nextProps.data.length) return false;

    // Comparación profunda simplificada para evitar re-renderizados innecesarios
    // Solo consideramos cambios en title, target y amount que afectan a la visualización
    const prevDataString = JSON.stringify(
        prevProps.data.map(item => ({
            title: item.title,
            target: item.target,
            amount: item.amount
        }))
    );

    const nextDataString = JSON.stringify(
        nextProps.data.map(item => ({
            title: item.title,
            target: item.target,
            amount: item.amount
        }))
    );

    return prevDataString === nextDataString;
};

// Exportar componente con memoización avanzada
export default React.memo(CardGraphOutHour, arePropsEqual);
